import { Loading } from '@farmshare/ui-components';
import { Box, Stack, Typography } from '@mui/material';
import { map } from 'lodash';
import { useEffect } from 'react';

import { ProducerCutsheetDrawer } from '../../shared/cutsheet-drawer';
import { useOrderDrawerActions } from '../../shared/hooks/use-order-drawer-actions';
import { ProducerQuestionnaireEditWizard } from '../../shared/questionnaire-edit-wizard';
import { ProducerQuestionnaireResponseDrawer } from '../../shared/questionnaire-response-drawer';
import type {
  CutsheetInfo as QuestionnaireCutsheetInfo,
  Job as QuestionnaireJob,
} from '../../shared/split-slot/types';
import { orderCardGridSx } from '../../shared/styles';

import { CutsheetWizardForm } from 'features/capabilities/cutsheets/wizard/form/cutsheet-wizard-form';

import { BuyerOrderCard } from '../../buyer/buyer-order-card';
import { useBuyerOrders } from '../../buyer/use-buyer-orders';

interface ProducerOrdersTabProps {
  onCountChange?: (count: number) => void;
}

export function ProducerOrdersTab({ onCountChange }: ProducerOrdersTabProps) {
  const { user, activeOrders, completedOrders, loading, refetchOrders } =
    useBuyerOrders({ excludeOwnJobs: true });

  const totalCount = activeOrders.length + completedOrders.length;
  useEffect(() => {
    onCountChange?.(totalCount);
  }, [totalCount, onCountChange]);

  const {
    drawerCtx,
    setDrawerCtx,
    drawerJob,
    drawerHead,
    drawerCutsheetInfo,
    questionnaireResponseDrawerCtx,
    setQuestionnaireResponseDrawerCtx,
    questionnaireEditWizardCtx,
    setQuestionnaireEditWizardCtx,
    wizardCtx,
    setWizardCtx,
    wizardDataReady,
    wizardCapabilities,
    wizardSettings,
    templateCutsheetRef,
    wizardAnimalSpecies,
    wizardInspectionLevel,
    generatingPdf,
    handleFillCutsheet,
    handleEditCutsheet,
    handleDownloadPdf,
    handleWizardCutsheetCreated,
    handleDrawerCreateNew,
  } = useOrderDrawerActions({
    userId: user?._id ?? '',
    refetchOrders,
  });

  if (!user) return <Loading />;

  const hasActiveOrders = activeOrders.length > 0;
  const hasCompletedOrders = completedOrders.length > 0;
  const isEmpty = !loading && !hasActiveOrders && !hasCompletedOrders;

  return (
    <Box>
      {hasActiveOrders && (
        <Box sx={{ mb: 4 }}>
          <Typography variant="h6" fontWeight={600} sx={{ pl: 1, mb: 1.5 }}>
            Active Orders
          </Typography>
          <Box sx={orderCardGridSx}>
            {map(activeOrders, (item) => (
              <BuyerOrderCard
                key={item.key}
                item={item}
                generatingPdf={generatingPdf}
                onFillCutsheet={handleFillCutsheet}
                onEditCutsheet={handleEditCutsheet}
                onDownloadPdf={handleDownloadPdf}
              />
            ))}
          </Box>
        </Box>
      )}

      {hasCompletedOrders && (
        <Box>
          <Typography variant="h6" fontWeight={600} sx={{ pl: 1, mb: 1.5 }}>
            Inactive Orders
          </Typography>
          <Box sx={orderCardGridSx}>
            {map(completedOrders, (item) => (
              <BuyerOrderCard
                key={item.key}
                item={item}
                generatingPdf={generatingPdf}
                completed
                onFillCutsheet={handleFillCutsheet}
                onEditCutsheet={handleEditCutsheet}
                onDownloadPdf={handleDownloadPdf}
              />
            ))}
          </Box>
        </Box>
      )}

      {isEmpty && (
        <Stack alignItems="center" sx={{ py: 8 }}>
          <Typography variant="body1" color="text.secondary">
            You don&apos;t have any orders yet.
          </Typography>
        </Stack>
      )}

      {loading && !hasActiveOrders && !hasCompletedOrders && <Loading />}

      {drawerJob && drawerHead && drawerCutsheetInfo && (
        <ProducerCutsheetDrawer
          open={drawerCtx != null}
          onClose={() => setDrawerCtx(null)}
          onCutsheetAssigned={refetchOrders}
          onCreateNew={handleDrawerCreateNew}
          job={drawerJob}
          head={drawerHead}
          cutsheetInfo={drawerCutsheetInfo}
        />
      )}

      {wizardCtx != null && (
        <CutsheetWizardForm
          open={wizardCtx != null && wizardDataReady}
          onClose={() => {
            setWizardCtx(null);
            templateCutsheetRef.current = undefined;
          }}
          templateCutsheet={templateCutsheetRef.current}
          capabilities={wizardCapabilities}
          settings={wizardSettings}
          animalSpecies={wizardAnimalSpecies}
          inspectionLevel={wizardInspectionLevel}
          cutsheetType="buyer"
          buyerId={user._id}
          producerId={wizardCtx.item.job.requestedBy?._id ?? undefined}
          userId={user._id}
          onSubmitWithCutsheet={(cutsheet) => {
            handleWizardCutsheetCreated(cutsheet);
          }}
        />
      )}

      {questionnaireResponseDrawerCtx && (
        <ProducerQuestionnaireResponseDrawer
          open
          onClose={() => setQuestionnaireResponseDrawerCtx(null)}
          onResponseAssigned={refetchOrders}
          job={
            questionnaireResponseDrawerCtx.item
              .job as unknown as QuestionnaireJob
          }
          cutsheetInfo={
            questionnaireResponseDrawerCtx.slot
              .raw as unknown as QuestionnaireCutsheetInfo
          }
        />
      )}

      {questionnaireEditWizardCtx && (
        <ProducerQuestionnaireEditWizard
          open
          onClose={() => {
            setQuestionnaireEditWizardCtx(null);
            refetchOrders();
          }}
          job={
            questionnaireEditWizardCtx.item.job as unknown as QuestionnaireJob
          }
          cutsheetInfo={
            questionnaireEditWizardCtx.slot
              .raw as unknown as QuestionnaireCutsheetInfo
          }
        />
      )}
    </Box>
  );
}
